import { useEffect, useState } from "react";
import ResultView from "./ResultView";
import type { CheckResult } from "../types";

const MAX_TEXT = 6000;

export default function CheckForm() {
  const [username, setUsername] = useState("");
  const [prototypeUrl, setPrototypeUrl] = useState("");
  const [password, setPassword] = useState("");
  const [summaryText, setSummaryText] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CheckResult | null>(null);

  const remaining = MAX_TEXT - summaryText.length;

  const runCheck = async (override?: {
    username?: string;
    prototypeUrl?: string;
    password?: string;
    summaryText?: string;
    files?: File[];
  }) => {
    setError(null);
    setResult(null);
    const payload = {
      username: override?.username ?? username,
      prototypeUrl: override?.prototypeUrl ?? prototypeUrl,
      password: override?.password ?? password,
      summaryText: override?.summaryText ?? summaryText,
      files: override?.files ?? files
    };

    if (!payload.username || !payload.prototypeUrl) {
      setError("请填写用户名和原型链接");
      return;
    }
    setLoading(true);
    try {
      let res: Response;
      if (payload.files.length > 0) {
        const form = new FormData();
        form.append("username", payload.username);
        form.append("prototypeUrl", payload.prototypeUrl);
        if (payload.password) form.append("password", payload.password);
        if (payload.summaryText) form.append("summaryText", payload.summaryText);
        payload.files.forEach((f) => form.append("files", f));
        res = await fetch("/api/check", {
          method: "POST",
          body: form
        });
      } else {
        res = await fetch("/api/check", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            username: payload.username,
            prototypeUrl: payload.prototypeUrl,
            password: payload.password || undefined,
            summaryText: payload.summaryText || undefined
          })
        });
      }
      if (!res.ok) {
        const message = await res.text();
        throw new Error(message || "检查失败");
      }
      const data: CheckResult = await res.json();
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "请求失败");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    runCheck();
  };

  const dataUrlToFile = (dataUrl: string, filename: string) => {
    const [meta, base64] = dataUrl.split(",");
    const match = /data:(.*?);base64/.exec(meta || "");
    const mime = match?.[1] || "image/png";
    const binary = atob(base64 || "");
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
    return new File([bytes], filename, { type: mime });
  };

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== "PRDCHECK_IMPORT") return;
      const payload = data.payload || {};
      const importedFiles = (payload.images || []).map((url: string, idx: number) =>
        dataUrlToFile(url, `capture-${idx + 1}.png`)
      );
      setUsername(payload.username || "");
      setPrototypeUrl(payload.prototypeUrl || "");
      setPassword(payload.password || "");
      setSummaryText(payload.summaryText || "");
      setFiles(importedFiles);
      // 直接触发检查
      runCheck({
        username: payload.username || "",
        prototypeUrl: payload.prototypeUrl || "",
        password: payload.password || "",
        summaryText: payload.summaryText || "",
        files: importedFiles
      });
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, []);

  return (
    <div>
      <h2 className="section-title">提交需求文档信息</h2>
      <form onSubmit={handleSubmit} className="form-grid">
        <div className="field">
          <label>用户名</label>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="请输入产品/需求负责人"
            required
          />
        </div>
        <div className="field">
          <label>Axure 原型链接</label>
          <input
            value={prototypeUrl}
            onChange={(e) => setPrototypeUrl(e.target.value)}
            placeholder="https://xxxxx.axshare.com/..."
            required
          />
        </div>
        <div className="field">
          <label>访问密码（如有）</label>
          <input
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="可选"
          />
        </div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label>补充需求描述/要点（建议粘贴关键文本，提升准确度）</label>
          <textarea
            value={summaryText}
            onChange={(e) => setSummaryText(e.target.value)}
            placeholder="例如：核心流程、主要交互、边界条件、数据字段、验收标准等"
            maxLength={MAX_TEXT}
          />
          <span className="muted small">剩余字数：{remaining}</span>
        </div>
        <div className="field" style={{ gridColumn: "1 / -1" }}>
          <label>上传原型截图（PNG/JPG/WEBP，最多 12 张，单张 ≤ 3MB）</label>
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={(e) => setFiles(Array.from(e.target.files || []))}
          />
          {files.length > 0 && (
            <div className="muted small">
              已选择：{files.map((f) => f.name).join(", ")}
            </div>
          )}
        </div>
        <div className="actions" style={{ gridColumn: "1 / -1" }}>
          <button className="btn primary" type="submit" disabled={loading}>
            {loading ? "检查中..." : "开始检查"}
          </button>
          <button
            className="btn outline"
            type="button"
            onClick={() => {
              setUsername("");
              setPrototypeUrl("");
              setPassword("");
              setSummaryText("");
              setFiles([]);
              setResult(null);
              setError(null);
            }}
          >
            清空
          </button>
        </div>
      </form>
      {error && <div className="danger" style={{ marginTop: 12 }}>{error}</div>}
      {result && (
        <div style={{ marginTop: 20 }}>
          <ResultView result={result} />
        </div>
      )}
    </div>
  );
}
